import { useCallback, useMemo } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { Button } from "semantic-ui-react";

import api, { asQuery } from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import TagModalChooser from "components/lib/Choosers/Tags/TagModalChooser";

const IdeaTags = ({ updateIdea, idea, isSubmission = false }) => {
  const user = useAppSelector((state) => state.user);
  const challengeQuery = useQuery({
    queryKey: ["challenges", idea?.challenge],
    queryFn: asQuery(api.challenges.get, { params: [idea?.challenge] }),
    enabled: !!idea?.challenge,
  });
  const challenge = challengeQuery.data;

  const currentChallenge = challenge || idea.ownerChallenge;

  const { t } = useTranslation();
  const canEdit = useMemo(
    () =>
      util.canEditIdea(user, idea) ||
      util.canManageChallenge(user, currentChallenge) ||
      util.hasPermission(user, "challenge.editIdeaTags", currentChallenge?._id),
    [user, idea, currentChallenge],
  );

  const updateTagListWithTag = useCallback(
    (tag) => {
      const tagList = [...currentChallenge.tagList];
      const index = tagList.findIndex((t) => t._id === tag._id);
      tagList[index] = tag;

      if (challenge) {
        api.queryClient.setQueryData(
          ["challenges", challenge._id, "tagList"],
          (prevChallenge) => ({ ...(prevChallenge || {}), tagList }),
        );
      } else {
        updateIdea({ ...currentChallenge, tagList }, ["ownerChallenge"], true);
      }
    },
    [updateIdea, currentChallenge, challenge],
  );

  const addTag = useCallback(
    (tag) => {
      api.tags.tagSingle(
        "ideas",
        tag._id,
        idea._id,
        (updatedTag) => {
          updateIdea(
            [
              ...(idea.ownerTags || []).filter((t) => t._id !== updatedTag._id),
              updatedTag,
            ],
            ["ownerTags"],
            true,
          );
          updateIdea(
            [
              ...(idea.tags || []).filter((t) => t !== updatedTag._id),
              updatedTag._id,
            ],
            ["tags"],
            true,
          );
          updateTagListWithTag(updatedTag);
        },
        (err) => toast.error(err.message),
      );
    },
    [idea, updateIdea, updateTagListWithTag],
  );

  const removeTag = useCallback(
    (tag) => {
      api.tags.untagSingle("ideas", tag._id, idea._id, (updatedTag) => {
        updateIdea(
          [...idea.ownerTags.filter((t) => t._id !== updatedTag._id)],
          ["ownerTags"],
          true,
        );
        updateIdea(
          [...idea.tags.filter((t) => t !== updatedTag._id)],
          ["tags"],
          true,
        );
        updateTagListWithTag(updatedTag);
      });
    },
    [idea, updateIdea, updateTagListWithTag],
  );

  if (
    !currentChallenge ||
    !currentChallenge.tagList ||
    currentChallenge.tagList.length === 0
  ) {
    return null;
  }
  return (
    <TagModalChooser
      trigger={
        !isSubmission && (
          <Button
            compact
            icon="hashtag"
            content={t("tags.edit")}
            size="tiny"
            style={{ marginLeft: 3 }}
          />
        )
      }
      showTags
      tags={idea.ownerTags}
      availableTags={currentChallenge.tagList.filter(
        (tag) => !tag?.disabledEntities?.includes("ideas"),
      )}
      addTag={addTag}
      removeTag={removeTag}
      canEdit={canEdit}
    />
  );
};

export default IdeaTags;
